// Which watchlist rows get their cached TMDB show_data re-pulled at boot.
//
// The boot sweep (useWatchlist.sweepStaleSchedules) can only afford a handful of TMDB
// calls, and the stale predicate is deliberately broad: a completed show with no cached
// next episode ALWAYS reads as stale (that's how revivals get noticed). On a mature
// list that means dozens of finished shows compete for the slots with the few shows
// that are actually airing this week, and in list order the finished ones win every
// boot. Jason, 2026-09-26: Deadliest Catch (caught up, next ep cached as Sep 18) never
// refreshed, so Tonight / Upcoming never learned E19 aired Sep 25. 57 completed + 29
// caught-up stale rows for a 15-row cap.
//
// Order of service:
//   1. watching / rewatching / caught_up rows whose cached next date has slipped into
//      the past — most recent first. These are the shows most likely airing right now.
//   2. the same statuses with no cached next episode at all.
//   3. completed rows, rotated by day so every finished show gets a turn within a
//      couple of weeks instead of the same first N forever.
//
// Pure: takes rows + today + a day index, returns the rows to refresh in order.

const REFRESHABLE = new Set(['watching', 'rewatching', 'caught_up', 'completed'])

export const SWEEP_CAP = 20

export function isStaleRow(r, today) {
  if (!r) return false
  if (isStubRow(r)) return true
  if (!REFRESHABLE.has(r.status)) return false
  const air = r.show_data && r.show_data.next_episode_to_air && r.show_data.next_episode_to_air.air_date
  return !air || air <= today
}

// A row whose show_data is a search-result stub (no seasons, no status, no episode
// markers) is not merely stale, it is blind: revival detection, "aired" gates and
// season rolls all read nothing. Those go first, whatever their status.
export const isStubRow = (r) => !!r?.show_data && !Array.isArray(r.show_data.seasons) && !r.show_data.status

export function pickStaleRows(rows, today, dayIndex = 0, cap = SWEEP_CAP) {
  const stale = (rows || []).filter(r => isStaleRow(r, today))
  const stubs = stale.filter(isStubRow)
  const active = stale.filter(r => !isStubRow(r) && r.status !== 'completed')
  const done = stale.filter(r => !isStubRow(r) && r.status === 'completed')
  const nextDate = r => (r.show_data && r.show_data.next_episode_to_air && r.show_data.next_episode_to_air.air_date) || ''
  // Dated (recently past) first, newest date first; undated after.
  active.sort((a, b) => {
    const da = nextDate(a), db = nextDate(b)
    if (da && !db) return -1
    if (!da && db) return 1
    return db.localeCompare(da)
  })
  let rotated = done
  if (done.length > 1) {
    const rot = ((dayIndex % done.length) + done.length) % done.length
    rotated = done.slice(rot).concat(done.slice(0, rot))
  }
  return stubs.concat(active, rotated).slice(0, cap)
}
